import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import { Txn, Category, Group, Budget } from '../money.model';
import {
  budgetRows, groupBudgetRollup, groupCategoriesForBudget,
  BudgetRow, GroupBudgetRow, CategoryGroupRow, centsToDraft, budgetChanged,
  hasOverrideForMonth, suggestBudgets,
} from './budgets.calc';
import { spendableTxns, monthKey } from '../dashboard/dashboard.aggregate';
import { formatCents } from '../format';

@Component({
  selector: 'app-money-budgets',
  templateUrl: './budgets.component.html',
  styleUrls: ['./budgets.component.scss'],
  standalone: false,
})
export class BudgetsComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';
  months: string[] = [];
  month = '';
  rows: BudgetRow[] = [];
  rollup: GroupBudgetRow[] = [];
  categories: Category[] = [];
  categoryGroups: CategoryGroupRow[] = [];
  /** Dollar strings bound to the default-amount inputs, keyed by category id. */
  draft: Record<string, string> = {};
  /**
   * Dollar strings bound to the override inputs, keyed by category id, for
   * whichever month is currently displayed. Reseeded whenever `month`
   * changes (see seedOverrideDrafts) since a stale entry would show last
   * month's override text against this month's row.
   */
  overrideDraft: Record<string, string> = {};

  /** Category ids whose current draft came from a suggestion, for the hint styling. */
  suggested = new Set<string>();

  readonly fmt = formatCents;

  private uid = '';
  private all: Txn[] = [];
  private groups: Group[] = [];
  private budgets: Record<string, Budget> = {};
  private subs: Subscription[] = [];
  /** The month overrideDraft was last seeded for; drives the reseed-on-change in seedOverrideDrafts. */
  private overrideDraftMonth = '';

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.uid = user.uid;
      this.subs.push(this.money.subscribeAll(this.uid).subscribe({
        next: data => {
          this.all = data.txns;
          this.groups = data.groups;
          this.categories = data.categories;
          this.budgets = data.budgets;

          this.months = [...new Set(spendableTxns(data.txns).map(t => monthKey(t.date)))]
            .sort().reverse();
          if (!this.month || !this.months.includes(this.month)) {
            this.month = this.months[0] ?? '';
          }

          this.categories.forEach(c => {
            const b = this.budgets[c.id];
            // Only seed a draft the user has not started editing.
            if (this.draft[c.id] === undefined) {
              this.draft[c.id] = b ? centsToDraft(b.amount) : '';
            }
          });

          this.recompute();
          this.loading = false;
        },
        error: () => { this.error = 'Could not load your ledger.'; this.loading = false; },
      }));
    }));
  }

  ngOnDestroy(): void { this.subs.forEach(s => s.unsubscribe()); }

  private recompute(): void {
    this.rows = budgetRows(this.all, this.month, this.categories, this.budgets);
    this.rollup = groupBudgetRollup(this.rows, this.categories, this.groups);
    this.categoryGroups = groupCategoriesForBudget(this.groups, this.categories);
    this.seedOverrideDrafts();
  }

  /**
   * Seeds overrideDraft for the currently displayed month. A month change
   * invalidates every entry (last month's override text has nothing to do
   * with this month's row), so the whole map is cleared and reseeded; within
   * one month, an entry the user has started editing (already present in
   * the map) is left alone so an in-flight edit survives an unrelated
   * subscribeAll emission — same rule as the default-amount `draft` above.
   */
  private seedOverrideDrafts(): void {
    if (this.overrideDraftMonth !== this.month) {
      this.overrideDraft = {};
      this.overrideDraftMonth = this.month;
    }
    this.categories.forEach(c => {
      if (this.overrideDraft[c.id] === undefined) {
        const override = this.budgets[c.id]?.overrides?.[this.month];
        this.overrideDraft[c.id] = override !== undefined && override !== null
          ? centsToDraft(override) : '';
      }
    });
  }

  get monthLabel(): string {
    return this.month
      ? new Date(`${this.month}-01T00:00:00`)
          .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : '';
  }

  get canGoBack(): boolean { return this.monthIndex < this.months.length - 1; }
  get canGoForward(): boolean { return this.monthIndex > 0; }
  private get monthIndex(): number { return this.months.indexOf(this.month); }

  // months is newest-first, so "previous" is the next index along — same
  // convention as the dashboard's month nav.
  prevMonth(): void {
    if (this.canGoBack) { this.month = this.months[this.monthIndex + 1]; this.recompute(); }
  }

  nextMonth(): void {
    if (this.canGoForward) { this.month = this.months[this.monthIndex - 1]; this.recompute(); }
  }

  rowFor(categoryId: string): BudgetRow | undefined {
    return this.rows.find(r => r.categoryId === categoryId);
  }

  isOverridden(categoryId: string): boolean {
    return hasOverrideForMonth(this.budgets[categoryId], this.month);
  }

  /**
   * Returns whether the category ended up saved (or already matched, so
   * there was nothing to write) versus failed. saveSuggestions relies on
   * this to know when to stop the loop rather than plough on and report a
   * partial failure as a full success (Finding 4).
   */
  async save(categoryId: string): Promise<boolean> {
    const raw = (this.draft[categoryId] ?? '').trim();
    const dollars = raw === '' ? null : Number(raw);
    if (dollars !== null && (!isFinite(dollars) || dollars < 0)) {
      this.error = 'Enter a positive amount, or leave it blank to remove the budget.';
      return false;
    }
    this.error = '';
    const centsToWrite = dollars === null ? null : Math.round(dollars * 100);
    const storedCents = this.budgets[categoryId]?.amount;
    if (!budgetChanged(centsToWrite, storedCents)) {
      return true; // No change, skip the write.
    }
    try {
      await this.money.setBudget(this.uid, categoryId, centsToWrite);
      // Persisted: it's no longer an unsaved suggestion, so drop the gold
      // border and stop counting it in the "N suggestions" hint. This is the
      // ONLY place a suggestion is cleared — saveSuggestions must not also
      // clear it unconditionally, or a failed write still looks persisted.
      this.suggested.delete(categoryId);
      return true;
    }
    catch {
      this.error = 'Could not save that budget.';
      return false;
    }
  }

  private todayLocal(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /**
   * Fills empty default-budget drafts with a suggestion drawn from the
   * trailing six completed months of actual spend. Only fills holes: a
   * draft the user has already typed, or a stored budget that already
   * exists (which seeded the draft on load), wins over a suggestion.
   * Nothing is written until saveSuggestions (or a per-row save) is called.
   */
  suggest(): void {
    const suggestions = suggestBudgets(this.all, this.categories, this.groups, this.todayLocal());
    Object.entries(suggestions).forEach(([id, cents]) => {
      // Only fill holes: a draft the user typed, or a stored budget, wins.
      if ((this.draft[id] ?? '') === '') {
        this.draft[id] = centsToDraft(cents);
        this.suggested.add(id);
      }
    });
  }

  /**
   * Stops on the first failure rather than ploughing through the rest: save()
   * already owns removing a category from `suggested` on its own success
   * path, and already leaves `this.error` set on failure. Looping past a
   * failure would call save() again, which resets `this.error = ''` at its
   * top — wiping the very error this loop needs the user to see.
   */
  async saveSuggestions(): Promise<void> {
    for (const id of [...this.suggested]) {
      const ok = await this.save(id);   // the existing per-row write path, unchanged
      if (!ok) break;
    }
  }

  /**
   * Save (or clear) the override for the displayed month. An empty input
   * clears the override — falling back to the default, not to nothing,
   * because budgetPatch writes `null` at the override path rather than
   * deleting the whole budget node.
   */
  async saveOverride(categoryId: string): Promise<void> {
    const raw = (this.overrideDraft[categoryId] ?? '').trim();
    const dollars = raw === '' ? null : Number(raw);
    if (dollars !== null && (!isFinite(dollars) || dollars < 0)) {
      this.error = 'Enter a positive amount, or leave it blank to use the default.';
      return;
    }
    this.error = '';
    const centsToWrite = dollars === null ? null : Math.round(dollars * 100);
    const storedCents = this.budgets[categoryId]?.overrides?.[this.month];
    if (!budgetChanged(centsToWrite, storedCents)) {
      return; // No change, skip the write.
    }
    try { await this.money.setBudget(this.uid, categoryId, centsToWrite, this.month); }
    catch { this.error = 'Could not save that override.'; }
  }

  trackCategory(_: number, c: Category): string { return c.id; }
  trackGroupRow(_: number, g: GroupBudgetRow): string { return g.groupId; }
  trackCategoryGroup(_: number, g: CategoryGroupRow): string { return g.group.id; }
}
